import { useEffect, useRef, useState } from "react";
import { getCsrfTokenCached } from "@/auth/csrf";

/**
 * /$locale/logout — submits a real `<form method="POST">` against
 * `/auth/logout` so the browser follows the 302 chain into Keycloak's
 * RP-initiated-logout endpoint and back to
 * `qrcode.bff.post-logout-redirect-uri` (the marketing landing on
 * prod, `/` on dev).
 *
 * Why not `fetch()`: an XHR with `redirect: "manual"` cannot follow
 * the 302 to Keycloak, so the previous version cleared the BFF
 * session cookie and then navigated the SPA to `/`. Keycloak's SSO
 * session was still alive, the route-loader's `/auth/me` probe came
 * back authenticated, and the user landed on `/codes` instead of
 * being actually logged out. A real form-submit closes that loop —
 * browser-level navigation honours every step of the redirect chain.
 */
export function LogoutRedirect() {
  const formRef = useRef<HTMLFormElement>(null);
  const [csrf, setCsrf] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getCsrfTokenCached().then((token) => {
      if (cancelled) return;
      setCsrf(token);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (csrf && formRef.current) {
      formRef.current.submit();
    }
  }, [csrf]);

  return (
    <main className="grid min-h-dvh place-items-center text-sm text-muted-foreground">
      Abmelden…
      <form ref={formRef} method="POST" action="/auth/logout" className="hidden">
        {csrf ? <input type="hidden" name="_csrf" value={csrf} /> : null}
      </form>
    </main>
  );
}
